import { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MangaCard from "@/components/feature/MangaCard";
import { useSeo } from "@/hooks/useSeo";
import { categories, categoryMap } from "@/mocks/categories";
import { mangaList } from "@/mocks/manga";

const BASE = "https://secretcomix.github.io";

const heroMap: Record<string, string> = {
  yuzhi: "bg-primary-100",
  gaoxiao: "bg-accent-100",
  ruanmeng: "bg-secondary-100",
  wenxin: "bg-primary-100",
  all: "bg-background-100",
};

export default function Category() {
  const { slug = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();

  const cat = slug === "all" ? null : categoryMap[slug];
  const name = cat ? cat.name : "全部作品";
  const desc = cat
    ? cat.description
    : "浏览喵趣漫画网收录的全部萌系日常、治愈番、搞笑萌漫、软萌画风与温馨向轻喜剧作品，轻松追更不踩雷。";

  const list = useMemo(() => {
    let base = slug === "all" ? [...mangaList] : mangaList.filter((m) => m.category === slug);
    if (q) {
      const kw = q.toLowerCase();
      base = base.filter(
        (m) =>
          m.title.toLowerCase().includes(kw) ||
          m.summary.toLowerCase().includes(kw) ||
          m.tags.some((t) => t.toLowerCase().includes(kw))
      );
    }
    return base;
  }, [slug, q]);

  const title = q
    ? `搜索“${q}”的萌系漫画 - 喵趣漫画网`
    : `${name}合集，轻松追更不踩雷 - 喵趣漫画网`;

  useSeo({
    title,
    description: `${desc} 喵趣漫画网为你精选${name}类轻松好看的萌系作品，治愈番、搞笑萌漫、软萌画风一网打尽。`,
    keywords: `喵趣漫画网,${name},萌系日常,治愈番,搞笑萌漫,软萌画风`,
    url: BASE + (slug === "all" ? "/category/all" : `/category/${slug}`),
    type: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: `${name} - 喵趣漫画网`,
        description: desc,
        url: BASE + (slug === "all" ? "/category/all" : `/category/${slug}`),
        inLanguage: "zh-CN",
        mainEntity: {
          "@type": "ItemList",
          itemListElement: list.slice(0, 8).map((m, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: m.title,
            url: BASE + `/manga/${m.slug}`,
          })),
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: BASE + "/" },
          { "@type": "ListItem", position: 2, name, item: BASE + (slug === "all" ? "/category/all" : `/category/${slug}`) },
        ],
      },
    ],
  });

  return (
    <Layout>
      <section className={`${heroMap[slug] || "bg-background-100"} py-10 md:py-14`}>
        <div className="container-page">
          <nav className="mb-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">{name}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">{name}合集</h1>
          <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-700">
            {q ? `为你找到“${q}”相关的 ${list.length} 部作品。` : desc}
          </p>
        </div>
      </section>

      <section className="container-page mt-8 md:mt-10">
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <Link
            to="/category/all"
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              slug === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
            }`}
          >
            全部
          </Link>
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                slug === c.slug ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-secondary-100"
              }`}
            >
              {c.name}
            </Link>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {list.map((m) => (
              <MangaCard key={m.slug} manga={m} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center rounded-2xl border border-background-200 bg-background-50 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-700">
              <i className="ri-search-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-600">没有找到相关作品，换个关键词试试吧。</p>
            <Link to="/category/all" className="btn-ghost mt-5">
              浏览全部作品
            </Link>
          </div>
        )}
      </section>
    </Layout>
  );
}